import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getKidsAvailabilityRequest, setKidsAvailability } from '../../utils/api';

// Kids "Most helpful 3" availability — a no-login page sent to a servant leader.
// The SL first picks HOW MANY sessions they can realistically serve, then marks
// WHICH sessions they're free for; the Kids team lead assigns from there.
// This flow is Kids-only and is intentionally NOT ported to the main app.

const TEAL = '#6AA292';
const ACCENT = '#FFDB7C';
const DAY_ORDER = ['Thursday', 'Friday', 'Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday'];

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDayDate(day, date) {
  if (!date) return day;
  try {
    const d = new Date(date + 'T00:00:00');
    return `${day} — ${d.toLocaleDateString('en-US', { month: 'long' })} ${d.getDate()}`;
  } catch { return day || date; }
}

// Mirrors the fixed session slots in the backend (KIDS_SESSION_SLOTS):
// numbered straight through the weekend, split by day and morning/evening.
const DEMO_DATA = {
  person: { id: 'demo', name: 'Jordan' },
  teamId: 'kids',
  teamName: 'Kids',
  sessions: [
    { blockId: 'Thursday|Evening', blockTitle: 'Session 1', day: 'Thursday', date: '', startTime: '', endTime: '', period: 'Evening' },
    { blockId: 'Friday|Morning', blockTitle: 'Session 2', day: 'Friday', date: '', startTime: '', endTime: '', period: 'Morning' },
    { blockId: 'Friday|Evening', blockTitle: 'Session 3', day: 'Friday', date: '', startTime: '', endTime: '', period: 'Evening' },
    { blockId: 'Saturday|Morning', blockTitle: 'Session 4', day: 'Saturday', date: '', startTime: '', endTime: '', period: 'Morning' },
    { blockId: 'Saturday|Evening', blockTitle: 'Session 5', day: 'Saturday', date: '', startTime: '', endTime: '', period: 'Evening' },
  ],
  existing: { maxSessions: 0, selected: [] },
};

export default function KidsAvailabilityPage() {
  const { personId } = useParams();
  // 'demo' plus the two kids SL previews all run on sample data; nothing saves.
  const isDemo = personId === 'demo' || personId === 'kids-sgl-demo' || personId === 'kids-sl-demo';
  const [data, setData] = useState(isDemo ? DEMO_DATA : null);
  const [loading, setLoading] = useState(!isDemo);
  const [error, setError] = useState(null);
  const [maxSessions, setMaxSessions] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Save failures show inline — the page-level `error` replaces the whole form
  // with "Link not found", which would throw away everything they filled in.
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    getKidsAvailabilityRequest(personId)
      .then(d => {
        if (cancelled) return;
        setData(d);
        setMaxSessions(d?.existing?.maxSessions || 0);
        setSelected(new Set(d?.existing?.selected || []));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('We couldn’t load your availability request. The link may have expired — contact your Kids team lead.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isDemo, personId]);

  function toggleSession(blockId) {
    setSaved(false);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  }

  async function handleSubmit() {
    setSaving(true);
    const payload = {
      personId: data.person.id,
      teamId: data.teamId,
      maxSessions,
      selected: Array.from(selected),
    };
    setSaveError('');
    try {
      if (!isDemo) await setKidsAvailability(payload);
      setSaved(true);
    } catch {
      setSaveError('Something went wrong saving your response. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--off)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 15, color: 'var(--muted)' }}>Loading…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--off)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 420, textAlign: 'center', padding: '0 24px' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 18, fontWeight: 900, color: 'var(--black)', marginBottom: 12 }}>Link not found</div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 15, color: 'var(--muted)', lineHeight: 1.6 }}>
            {error || 'This link is invalid or has expired. Contact your Kids team lead.'}
          </div>
        </div>
      </div>
    );
  }

  const sessions = (data.sessions || []).slice().sort((a, b) => {
    const da = DAY_ORDER.indexOf(a.day), db = DAY_ORDER.indexOf(b.day);
    if (da !== db) return (da === -1 ? 99 : da) - (db === -1 ? 99 : db);
    return (a.startTime || '').localeCompare(b.startTime || '');
  });
  const days = [];
  sessions.forEach(s => { if (!days.includes(s.day)) days.push(s.day); });
  const countOptions = Array.from({ length: Math.max(sessions.length, 1) }, (_, i) => i + 1);

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: 'var(--off)' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '40px 24px 80px' }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: TEAL, marginBottom: 8 }}>
            The Belonging Co · Kids Conference 2026
          </div>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 28, fontWeight: 900, color: 'var(--black)', lineHeight: 1.1 }}>
            Your Kids Availability
          </div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--muted)', marginTop: 4 }}>
            {data.teamName} · no login required
          </div>
        </div>

        {saved ? (
          <div className="avail-form-card" style={{ borderTop: `4px solid ${TEAL}` }}>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 18, fontWeight: 900, color: 'var(--black)', marginBottom: 8 }}>
              Thanks, {data.person.name}! ✓
            </div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--mid)', lineHeight: 1.6 }}>
              You told us you can serve up to <strong>{maxSessions || 0}</strong> session{maxSessions === 1 ? '' : 's'} and you’re free for <strong>{selected.size}</strong>. Your Kids team lead will build your schedule from here. You can come back to this link any time to update it.
            </div>
            <button className="btn btn-sm" style={{ marginTop: 16 }} onClick={() => setSaved(false)}>Edit my response</button>
          </div>
        ) : (
          <div className="avail-form-card" style={{ borderTop: `4px solid ${TEAL}` }}>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--mid)', marginBottom: 20, lineHeight: 1.6 }}>
              Hi {data.person.name}! First tell us <strong style={{ color: 'var(--black)' }}>how many</strong> Kids sessions you can realistically serve, then check <strong style={{ color: 'var(--black)' }}>which</strong> ones you’re free for. It’s totally fine to check more than your number — your team lead will pick the ones where you’re most helpful.
            </div>

            {/* How many */}
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>
              How many sessions can you serve?
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
              {countOptions.map(n => {
                const active = maxSessions === n;
                return (
                  <button
                    key={n}
                    onClick={() => { setMaxSessions(n); setSaved(false); }}
                    style={{
                      fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 15, minWidth: 44, padding: '10px 14px',
                      borderRadius: 'var(--radius)', cursor: 'pointer',
                      border: `2px solid ${active ? TEAL : 'var(--border)'}`,
                      background: active ? TEAL : 'var(--white)',
                      color: active ? '#fff' : 'var(--mid)',
                    }}
                  >
                    {n}
                  </button>
                );
              })}
            </div>

            {/* Which sessions */}
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>
              Which sessions are you free for?
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 20 }}>
              {days.map(day => (
                <div key={day}>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', padding: '12px 16px 8px', background: 'var(--off)', borderBottom: '1px solid var(--border)' }}>
                    {formatDayDate(day, sessions.find(s => s.day === day)?.date)}
                  </div>
                  {sessions.filter(s => s.day === day).map(s => {
                    const on = selected.has(s.blockId);
                    return (
                      <label
                        key={s.blockId}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer',
                          borderBottom: '1px solid var(--border)',
                          background: on ? 'rgba(106,162,146,0.10)' : 'var(--white)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleSession(s.blockId)}
                          style={{ width: 18, height: 18, accentColor: TEAL, flexShrink: 0 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 700, color: 'var(--black)' }}>{s.blockTitle}</div>
                          <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>
                            {[
                              s.startTime ? `${formatTime(s.startTime)}${s.endTime ? ` – ${formatTime(s.endTime)}` : ''}` : '',
                              s.period ? s.period.charAt(0).toUpperCase() + s.period.slice(1).toLowerCase() : '',
                            ].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ))}
              {sessions.length === 0 && (
                <div style={{ padding: '16px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>
                  No Kids sessions have been scheduled yet. Check back soon.
                </div>
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={saving || !maxSessions || selected.size === 0}
              style={{
                width: '100%', padding: '14px', fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 15,
                border: 'none', borderRadius: 'var(--radius)', cursor: (saving || !maxSessions || selected.size === 0) ? 'not-allowed' : 'pointer',
                background: (!maxSessions || selected.size === 0) ? 'var(--border)' : ACCENT,
                color: (!maxSessions || selected.size === 0) ? 'var(--muted)' : '#202020',
              }}
            >
              {saving ? 'Saving…' : 'Submit my availability'}
            </button>
            {saveError && (
              <div style={{ marginTop: 12, background: '#fff0f0', border: '1px solid #c00', borderRadius: 'var(--radius)', padding: '11px 14px', fontFamily: 'var(--font-b)', fontSize: 13, color: '#900', textAlign: 'center' }}>
                {saveError}
              </div>
            )}
            {(!maxSessions || selected.size === 0) && (
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', textAlign: 'center', marginTop: 10 }}>
                Pick a number and at least one session to submit.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
